import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  alpha,
  Paper,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import SportsEsportsIcon from '@mui/icons-material/SportsEsports'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement'
import ExtensionIcon from '@mui/icons-material/Extension'
import BubbleChartIcon from '@mui/icons-material/BubbleChart'
import {
  canManageEngagement,
  getBackgroundMusicTracks,
  getPatientEngagement,
  savePatientEngagement,
  resetPatientEngagement,
  getBookedEventEngagement,
  saveBookedEventEngagement,
  resetBookedEventEngagement,
  ENGAGEMENT_GAME_OPTIONS,
} from 'src/supportPortal/services/patientEngagementService'
import { toast } from 'react-toastify'

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    maxWidth: 560,
    width: '100%',
  },
}))

const SectionTitle = styled(Box)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 600,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}))

const CompactSelect = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    fontSize: '13px',
  },
  '& .MuiOutlinedInput-root': {
    fontSize: '13px',
  },
}))

const InfoBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: alpha('#8b5cf6', 0.05),
  border: '1px solid',
  borderColor: alpha('#8b5cf6', 0.2),
  borderRadius: theme.spacing(1),
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
}))

// Icon map for games
const GAME_ICONS = {
  music: MusicNoteIcon,
  breathing: SelfImprovementIcon,
  memory: ExtensionIcon,
  bubbles: BubbleChartIcon,
}

/**
 * EngagementSettingsDialog - Configure waiting screen engagement for patients or booked events
 * 
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close handler
 * @param {string} targetType - 'patient' or 'booked-event'
 * @param {object} target - The patient or booked event object
 * @param {function} onSuccess - Success callback
 */
export default function EngagementSettingsDialog({
  open,
  onClose,
  targetType = 'patient', // 'patient' or 'booked-event'
  target,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [enabled, setEnabled] = useState(false)
  const [selectedTracks, setSelectedTracks] = useState([])
  const [selectedGames, setSelectedGames] = useState(['music', 'breathing', 'memory', 'bubbles'])
  const [availableTracks, setAvailableTracks] = useState([])
  const [existingConfig, setExistingConfig] = useState(null)

  const hasPermission = useMemo(() => canManageEngagement(), [])

  const targetId = targetType === 'patient' ? target?.id : target?.bookedEventId || target?.id
  const targetName = targetType === 'patient' 
    ? target?.name || target?.patientName || 'Patient'
    : target?.eventName || target?.patientName || 'Booked Event'

  // Fetch available music tracks and existing config
  useEffect(() => {
    if (!open || !targetId) return

    const fetchData = async () => {
      setInitialLoading(true)
      try {
        // Fetch music tracks
        const tracksResult = await getBackgroundMusicTracks(true)
        if (tracksResult.success) {
          setAvailableTracks(tracksResult.tracks)
        }

        // Fetch existing config
        const configResult = targetType === 'patient'
          ? await getPatientEngagement(targetId)
          : await getBookedEventEngagement(targetId)

        if (configResult.success && configResult.config) {
          setExistingConfig(configResult.config)
          setEnabled(configResult.config.enabled || false)
          
          const engagement = configResult.config.engagement || {}
          
          // Set selected tracks
          if (engagement.music?.tracks) {
            setSelectedTracks(engagement.music.tracks.map(t => t.id))
          }
          
          // Set selected games
          if (engagement.games?.available) {
            setSelectedGames(engagement.games.available)
          }
        } else {
          // Reset to defaults if no config exists
          setExistingConfig(null)
          setEnabled(false)
          setSelectedTracks([])
          setSelectedGames(['music', 'breathing', 'memory', 'bubbles'])
        }
      } catch (error) {
        console.error('Error fetching engagement data:', error)
      } finally {
        setInitialLoading(false)
      }
    }

    fetchData()
  }, [open, targetId, targetType])

  // Build engagement object from selections
  const buildEngagementPayload = () => {
    const engagement = {
      games: {
        available: selectedGames,
      },
    }

    // Add music tracks if any selected
    if (selectedTracks.length > 0) {
      engagement.music = {
        tracks: selectedTracks.map(trackId => {
          const track = availableTracks.find(t => t.id === trackId)
          return {
            id: track?.id || trackId,
            name: track?.name || 'Unknown Track',
            mediaId: track?.mediaId || null,
          }
        }),
      }
    }

    return engagement
  }

  // Handle save
  const handleSave = async () => {
    if (!targetId) return

    setLoading(true)
    try {
      const engagement = buildEngagementPayload()
      
      const result = targetType === 'patient'
        ? await savePatientEngagement(targetId, { enabled, engagement })
        : await saveBookedEventEngagement(targetId, { enabled, engagement })

      if (result.success) {
        toast.success(enabled ? 'Engagement enabled successfully' : 'Engagement settings saved')
        onSuccess?.()
        onClose()
      } else {
        toast.error(result.message || 'Failed to save settings')
      }
    } catch (error) {
      console.error('Error saving engagement:', error)
      toast.error('Failed to save engagement settings')
    } finally {
      setLoading(false)
    }
  }

  // Handle reset
  const handleReset = async () => {
    if (!targetId) return

    setLoading(true)
    try {
      const result = targetType === 'patient'
        ? await resetPatientEngagement(targetId)
        : await resetBookedEventEngagement(targetId)

      if (result.success) {
        toast.success('Engagement settings reset')
        setEnabled(false)
        setSelectedTracks([])
        setSelectedGames(['music', 'breathing', 'memory', 'bubbles'])
        setExistingConfig(null)
        onSuccess?.()
        onClose()
      } else {
        toast.error(result.message || 'Failed to reset settings')
      }
    } catch (error) {
      console.error('Error resetting engagement:', error)
      toast.error('Failed to reset engagement settings')
    } finally {
      setLoading(false)
    }
  }

  if (!hasPermission) {
    return (
      <StyledDialog open={open} onClose={onClose}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SportsEsportsIcon sx={{ color: '#8b5cf6' }} />
              <Typography variant="h6" fontWeight={600}>
                Waiting Engagement
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            You don't have permission to manage engagement settings. This feature requires L2/L3 Therapy Support or Clinic roles.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </StyledDialog>
    )
  }

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: enabled ? 'rgba(139, 92, 246, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SportsEsportsIcon sx={{ color: enabled ? '#8b5cf6' : '#64748b', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Waiting Engagement
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {targetName}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {initialLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: '#8b5cf6' }} />
          </Box>
        ) : (
          <>
            {/* Info Box */}
            <InfoBox sx={{ mb: 2 }}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: '#8b5cf6', mt: 0.25 }} />
              <Box>
                <Typography variant="caption" fontWeight={600} sx={{ color: '#8b5cf6' }}>
                  {targetType === 'patient' ? 'Patient-Level Settings' : 'Session-Level Settings'}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  {targetType === 'patient'
                    ? 'These settings apply to all sessions for this patient. Session-level settings will override these.'
                    : 'These settings apply only to this session and override patient-level settings.'}
                </Typography>
              </Box>
            </InfoBox>

            {/* Main Toggle */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: enabled ? 'rgba(139, 92, 246, 0.05)' : 'rgba(100, 116, 139, 0.05)',
                border: '1px solid',
                borderColor: enabled ? 'rgba(139, 92, 246, 0.2)' : 'rgba(100, 116, 139, 0.2)',
                mb: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#8b5cf6',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#8b5cf6',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {enabled ? 'Engagement Enabled' : 'Engagement Disabled'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {enabled
                        ? 'Patient will see games & music while waiting (if wait > 2 mins)'
                        : 'Enable to show entertainment during waiting period'}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
            </Box>

            {/* Settings (only show when enabled) */}
            {enabled && (
              <>
                <Divider sx={{ my: 2 }} />

                {/* Music Tracks */}
                <SectionTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MusicNoteIcon sx={{ fontSize: 16 }} />
                    Music Tracks
                  </Box>
                </SectionTitle>
                <CompactSelect size="small" fullWidth>
                  <InputLabel>Select Music Tracks</InputLabel>
                  <Select
                    multiple
                    value={selectedTracks}
                    onChange={(e) => setSelectedTracks(e.target.value)}
                    input={<OutlinedInput label="Select Music Tracks" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((trackId) => {
                          const track = availableTracks.find(t => t.id === trackId)
                          return (
                            <Chip
                              key={trackId}
                              label={track?.name || trackId}
                              size="small"
                              sx={{ height: 22, fontSize: '11px' }}
                            />
                          )
                        })}
                      </Box>
                    )}
                  >
                    {availableTracks.length === 0 ? (
                      <MenuItem disabled>
                        <Typography variant="caption" color="text.secondary">
                          No music tracks available
                        </Typography>
                      </MenuItem>
                    ) : (
                      availableTracks.map((track) => (
                        <MenuItem key={track.id} value={track.id} dense>
                          <Checkbox checked={selectedTracks.includes(track.id)} size="small" />
                          <ListItemText 
                            primary={track.name} 
                            primaryTypographyProps={{ fontSize: '13px' }} 
                          />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </CompactSelect>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Select tracks for the music player. Leave empty for default tracks.
                </Typography>

                {/* Games */}
                <SectionTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SportsEsportsIcon sx={{ fontSize: 16 }} />
                    Available Games
                  </Box>
                </SectionTitle>
                <CompactSelect size="small" fullWidth>
                  <InputLabel>Select Games</InputLabel>
                  <Select
                    multiple
                    value={selectedGames}
                    onChange={(e) => setSelectedGames(e.target.value)}
                    input={<OutlinedInput label="Select Games" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((gameId) => {
                          const game = ENGAGEMENT_GAME_OPTIONS.find(g => g.value === gameId)
                          const Icon = GAME_ICONS[gameId] || SportsEsportsIcon
                          return (
                            <Chip
                              key={gameId}
                              icon={<Icon sx={{ fontSize: '14px !important' }} />}
                              label={game?.label || gameId}
                              size="small"
                              sx={{ height: 22, fontSize: '11px' }}
                            />
                          )
                        })}
                      </Box>
                    )}
                  >
                    {ENGAGEMENT_GAME_OPTIONS.map((game) => {
                      const Icon = GAME_ICONS[game.value] || SportsEsportsIcon
                      return (
                        <MenuItem key={game.value} value={game.value} dense>
                          <Checkbox checked={selectedGames.includes(game.value)} size="small" />
                          <Icon sx={{ fontSize: 18, mr: 1, color: '#8b5cf6' }} />
                          <ListItemText
                            primary={game.label}
                            secondary={game.description}
                            primaryTypographyProps={{ fontSize: '13px' }}
                            secondaryTypographyProps={{ fontSize: '11px' }}
                          />
                        </MenuItem>
                      )
                    })}
                  </Select>
                </CompactSelect>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Select which games/activities are available during the wait.
                </Typography>

                {/* Preview */}
                <SectionTitle sx={{ mt: 3 }}>Preview</SectionTitle>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Patient will see these options while waiting:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedGames.map((gameId) => {
                      const game = ENGAGEMENT_GAME_OPTIONS.find(g => g.value === gameId)
                      const Icon = GAME_ICONS[gameId] || SportsEsportsIcon
                      return (
                        <Chip
                          key={gameId}
                          icon={<Icon sx={{ fontSize: '16px !important' }} />}
                          label={game?.label || gameId}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: '#8b5cf6',
                            color: '#8b5cf6',
                            '& .MuiChip-icon': { color: '#8b5cf6' },
                          }}
                        />
                      )
                    })}
                  </Box>
                  {selectedTracks.length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Music: {selectedTracks.length} track{selectedTracks.length > 1 ? 's' : ''} selected
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {/* Reset button (only show if config exists) */}
        {existingConfig && (
          <Tooltip title="Remove all engagement settings">
            <Button
              variant="outlined"
              color="error"
              onClick={handleReset}
              disabled={loading}
              startIcon={<RestartAltIcon />}
              sx={{ mr: 'auto' }}
            >
              Reset
            </Button>
          </Tooltip>
        )}

        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || initialLoading}
          startIcon={loading && <CircularProgress size={16} color="inherit" />}
          sx={{
            backgroundColor: '#8b5cf6',
            '&:hover': { backgroundColor: '#7c3aed' },
          }}
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </DialogActions>
    </StyledDialog>
  )
}
